import React, { useState, useEffect } from 'react';
import '../../styles/admin/AdminIncomeAnalytics.css';
import { getRevenuePaymentLogs } from '../../utils/api';

const AdminIncomeAnalytics = () => {
  // 현재 선택된 년도와 월
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  
  // 로딩 및 오류 상태
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // API 데이터 저장
  const [yearlyData, setYearlyData] = useState({}); // 연간 데이터를 저장할 상태 추가
  const [monthlyData, setMonthlyData] = useState([]);
  const [totalYearlyIncome, setTotalYearlyIncome] = useState(0);
  const [companyCount, setCompanyCount] = useState(0);
  
  // 마지막으로 데이터를 가져온 연도
  const [lastFetchedYear, setLastFetchedYear] = useState(null);
  
  // 실제 현재 날짜 가져오기
  const realCurrentDate = new Date();
  const realCurrentMonth = realCurrentDate.getMonth() + 1;
  const realCurrentYear = realCurrentDate.getFullYear();
  
  // 연도가 변경됐거나 최초 로드 시 데이터 가져오기
  useEffect(() => {
    // 최초 로드 또는 연도 변경 시에만 API 호출
    if (lastFetchedYear !== currentYear) {
      fetchMonthlyData();
    } else {
      // 연도가 같고 월만 변경된 경우, 저장된 데이터에서 현재 월 데이터 업데이트
      updateCurrentMonthView();
    }
  }, [currentYear, currentMonth, lastFetchedYear]);
  
  // 빈 데이터 초기화 함수
  const initializeEmptyData = () => {
    const emptyMonthlyData = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      amount: 0,
      label: '0원',
      companyCount: 0
    }));
    
    setMonthlyData(emptyMonthlyData);
    setTotalYearlyIncome(0);
    setCompanyCount(0);
    setYearlyData({});
  };
  
  // API에서 데이터 가져오기
  const fetchMonthlyData = async () => {
    try {
      setLoading(true);
      
      // API 호출
      const response = await getRevenuePaymentLogs(currentYear, currentMonth);
      
      if (response && response.payment_logs) {
        const paymentLogs = response.payment_logs;
        console.log('Payment logs:', paymentLogs);
        
        // payment_logs를 월별로 그룹화하여 처리
        const groupedByMonth = {};
        let yearlyTotal = 0;
        
        // 1-12월 데이터 구조 초기화
        for (let i = 1; i <= 12; i++) {
          groupedByMonth[i] = {
            total_amount: 0,
            companies: new Set()
          };
        }
        
        // 모든 payment_logs 처리
        paymentLogs.forEach(log => {
          // 결제일 파싱
          const paymentDate = new Date(log.approved_at || log.created_at);
          const logYear = paymentDate.getFullYear();
          const month = paymentDate.getMonth() + 1; // 0부터 시작하므로 +1
          
          // 현재 조회 연도의 데이터만 사용
          if (logYear === currentYear) {
            // 결제 금액 합산
            const amount = log.amount || 0;
            yearlyTotal += amount;
            
            // 월별 그룹화
            if (!groupedByMonth[month]) {
              groupedByMonth[month] = {
                total_amount: 0,
                companies: new Set()
              };
            }
            
            groupedByMonth[month].total_amount += amount;
            
            // 회사 정보가 있으면 Set에 추가 (중복 제거)
            if (log.company_code) {
              groupedByMonth[month].companies.add(log.company_code);
            }
          }
        });
        
        // 연간 데이터 저장
        setYearlyData(groupedByMonth);
        
        // 월별 데이터 array로 변환
        const monthlyDataArray = [];
        for (let i = 1; i <= 12; i++) {
          const monthData = groupedByMonth[i] || { total_amount: 0, companies: new Set() };
          
          monthlyDataArray.push({
            month: i,
            amount: monthData.total_amount || 0,
            label: (monthData.total_amount || 0).toLocaleString() + '원',
            companyCount: monthData.companies ? monthData.companies.size : 0
          });
        }
        
        setMonthlyData(monthlyDataArray);
        setTotalYearlyIncome(yearlyTotal);
        
        // 현재 월 데이터 업데이트
        updateCompanyCount(monthlyDataArray);
        
        // 마지막으로 데이터를 가져온 연도 업데이트
        setLastFetchedYear(currentYear);
      } else {
        initializeEmptyData();
      }
    } catch (err) {
      console.error('수익 데이터 로드 오류:', err);
      setError(err.message);
      initializeEmptyData();
    } finally {
      setLoading(false);
    }
  };
  
  // 현재 월의 회사 수 업데이트
  const updateCompanyCount = (dataArray) => {
    const currentMonthData = dataArray.find(data => data.month === currentMonth);
    if (currentMonthData) {
      setCompanyCount(currentMonthData.companyCount);
    } else {
      setCompanyCount(0);
    }
  };
  
  // 저장된 데이터에서 현재 월 정보 업데이트
  const updateCurrentMonthView = () => {
    updateCompanyCount(monthlyData);
  };
  
  // 연간 총 수익 형식화
  const formattedYearlyTotal = totalYearlyIncome.toLocaleString();
  
  // 이전 월로 이동
  const goToPreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(prevYear => prevYear - 1);
    } else {
      setCurrentMonth(prevMonth => prevMonth - 1);
    }
  };
  
  // 다음 월로 이동
  const goToNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear(prevYear => prevYear + 1);
    } else {
      setCurrentMonth(prevMonth => prevMonth + 1);
    }
  };
  
  // 바 차트에서 가장 높은 값 찾기 (스케일링용)
  const maxAmount = Math.max(...monthlyData.map(item => item.amount), 1); // 최소값 1 설정
  
  // 현재 월의 데이터 가져오기
  const currentMonthData = monthlyData.find(data => data.month === currentMonth) || { amount: 0, label: '0원' };
  
  // 월 이름 텍스트 포맷팅 함수
  const getMonthDisplay = (month) => {
    // 실제 시스템 현재 월과 연도가 일치할 때만 "이번 달"로 표시
    if (month === realCurrentMonth && currentYear === realCurrentYear) {
      return '이번 달';
    }
    return `${month}월`;
  };

  if (loading && lastFetchedYear !== currentYear) {
    return <div className="loading-indicator">데이터를 불러오는 중...</div>;
  }
  if (error) return <div className="error-message">오류: {error}</div>;
  
  return (
    <div className="income-analytics-container">
      <div className="income-header">
        <div className="income-year-total">
          <h2 className="year-heading">{currentYear}년 총 수익</h2>
          <h1 className="income-total">{formattedYearlyTotal} 원</h1>
        </div>
      </div>
      
      <div className="income-chart-section">
        <div className="month-navigation">
          <button className="month-nav-button prev" onClick={goToPreviousMonth}>
            &lt;
          </button>
          <h2 className="month-display">{currentYear}년 {currentMonth}월</h2>
          <button className="month-nav-button next" onClick={goToNextMonth}>
            &gt;
          </button>
        </div>
        
        <div className="income-chart">
          {monthlyData.map((data) => {
            const isCurrentMonth = data.month === currentMonth;
            const isRealCurrentMonth = data.month === realCurrentMonth && currentYear === realCurrentYear;
            const hasData = data.amount > 0;
            
            return (
              <div className="chart-bar-container" key={data.month}>
                <div className="chart-bar-value">
                  {data.label}
                </div>
                <div 
                  className={`chart-bar ${isCurrentMonth ? 'current-month' : ''} ${!hasData && !isCurrentMonth ? 'no-data' : ''}`} 
                  style={{ 
                    height: hasData ? `${(data.amount / maxAmount) * 200}px` : '30px'
                  }}
                ></div>
                <div className="chart-bar-month">
                  {getMonthDisplay(data.month)}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="chart-divider"></div>
        
        <div className="monthly-details">
          <h3 className="month-heading">{currentYear}년 {currentMonth}월</h3>
          <div className="month-stats">
            <p className="month-stat">
              <span className="company-count">{companyCount}</span>개 업체로 부터
            </p>
            <p className="month-income">
              <span className="income-amount">{currentMonthData.label}</span> 수익이 발생했어요!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminIncomeAnalytics; 